// Coach to-do list — a lightweight "things I need to get done so nothing slips
// through the cracks" list, built on the LIVE coach_tasks backend (no migration).
// Primary capture point is the check-in review ("Add task"); its home is the
// My Tasks drawer off the sidebar. Tasks created here are UNSLOTTED (a due_date,
// no Power List slot), so task-list?backlog=1 returns them. This reuses the same
// table as the cut ADHD Power List page — it's the lean replacement.
import { getSupabase, getCoachTimezone, localDateStr } from './config.js';
import { showToast, escHtml, upgradeSelects } from './utils.js';
import { parseTask, mentionAt, TASK_TYPES } from './tasks/parse-task.js';
import { highlightSpans } from './tasks/highlight-spans.js';
import { groupTasks, TYPE_LABEL } from './tasks/group-tasks.js';
import { dueTag, subStatusPatch, subsOnStatusChange } from './tasks/task-rules.js';
import { caretOffset, setCaretOffset } from './tasks/caret.js';

// The composer (S76 rework). The suggestion list that used to float over the
// chip row — a document.body popup, hand-closed from ~8 call sites — is gone.
// It is now DERIVED on every render from _qaText/_qaCaret (see _qaParse,
// qaInput and the .ctk-quick block in renderTasksDrawer): if the caret isn't
// inside an in-progress @-token, the list simply isn't in the DOM. There is
// no popup lifecycle left to manage and nothing it can cover.
let _qaText = '';          // the raw sentence — the ONLY quick-add input state
let _qaCaret = 0;          // caret position, for the suggestion list
let _qaOff = new Set();    // claim kinds the coach switched off: 'client' |
                           // 'due' | 'type' | '__sugg' (suggestion list dismissed)
let _qaActive = 0;         // active index in the suggestion list
let _qaType = null;        // explicit type override — beats the parsed guess
let _qaDue = null;         // explicit due override — beats the parsed guess

let _tasks = [];
let _clients = [];

// Drawer state (S76). Quick-add affordances are all OPTIONAL — typing a title
// and hitting Enter still works with zero extra clicks.
let _filterClient = 'all'; // drawer client filter
let _showDone = false;     // completed-history view
let _noteEditId = null;    // task id with the inline notes editor open
let _subAddId = null;      // task id with the sub-task add input open
let _qaRecur = null;       // quick-add repeat rule: null | daily | weekly | monthly
let _qaMore = false;       // composer expanded (sub-items at creation)
let _qaSubs = [];          // composer sub-items: [{ id, title, done:false }]
let _groupBy = 'due';      // due | client | type — 'due' keeps today's behaviour

// The pivot: grouping is a VIEW, not a re-parenting — every task appears
// exactly once in every mode (groupTasks, tasks/group-tasks.js).
export function setGroupBy(mode) { _groupBy = mode; renderTasksDrawer(); }

const RECUR_CYCLE = [null, 'daily', 'weekly', 'monthly'];
const RECUR_LABEL = { daily: 'daily', weekly: 'weekly', monthly: 'monthly' };

export function setCoachTasksClients(c) { _clients = c || []; }

function _clientName(id) {
  if (!id) return '';
  const c = _clients.find(x => x.id === id);
  return c ? (c.data?.name || c.name || 'Client') : '';
}

// The parser wants [{id, name}]; _clients is a Supabase row shape. Flatten
// here so the parser never learns about the database.
function _parseClients() {
  return _clients
    .filter(c => c && c.id && !c.isSelfClient && c.status !== 'cancelled' && c.status !== 'pending_cancellation')
    .map(c => ({ id: c.id, name: _clientName(c.id) }));
}

// Everything about the quick-add is DERIVED from _qaText. No separate state
// can drift out of sync with it, which is what the popup did.
function _qaParse() {
  const r = parseTask(_qaText, { clients: _parseClients(), today: _today() });
  if (_qaOff.has('client')) r.clientId = null;
  if (_qaOff.has('due')) r.dueDate = null;
  if (_qaOff.has('type')) r.category = 'admin';
  if (_qaType) r.category = _qaType;
  if (_qaDue) r.dueDate = _qaDue;
  return r;
}

// ── API (task-list is GET; create/update/delete are POST) ──────────
async function _tapi(fn, { method = 'POST', body, query } = {}) {
  const supabase = getSupabase();
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  let url = `/.netlify/functions/${fn}`;
  if (query) url += '?' + new URLSearchParams(query).toString();
  const opts = { method, headers: { Authorization: `Bearer ${session.access_token}` } };
  if (method !== 'GET') { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body || {}); }
  const res = await fetch(url, opts);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Request failed');
  return data;
}

export async function loadCoachTasks() {
  try {
    const { tasks } = await _tapi('task-list', { method: 'GET', query: { backlog: '1', tz: getCoachTimezone() } });
    _tasks = tasks || [];
  } catch { /* leave stale; badge unaffected */ }
  _updateBadge();
  refreshClientTaskCounts();
  if (_drawerOpen()) renderTasksDrawer();
}

function _openCount() { return _tasks.filter(t => t.status !== 'done').length; }
function _updateBadge() {
  const b = document.getElementById('tasks-badge');
  if (b) {
    const n = _openCount();
    b.textContent = n;
    b.classList.toggle('hidden', n === 0);
  }
  _renderDashPill();
}

// Dashboard "needs you today" pill (#dash-tasks-pill, static in index.html).
// Shows only when something is overdue or due today — a quiet dashboard
// stays quiet. Click opens the drawer.
function _renderDashPill() {
  const el = document.getElementById('dash-tasks-pill');
  if (!el) return;
  const t = _today();
  const open = _tasks.filter(x => x.status !== 'done' && x.due_date);
  const overdue = open.filter(x => x.due_date < t).length;
  const today = open.filter(x => x.due_date === t).length;
  el.classList.toggle('hidden', !overdue && !today);
  if (!overdue && !today) return;
  const parts = [];
  if (overdue) parts.push(`${overdue} overdue`);
  if (today) parts.push(`${today} due today`);
  el.textContent = `Tasks: ${parts.join(' · ')}`;
}

// ── due-date helpers ───────────────────────────────────────────────
function _today() { return localDateStr(new Date(), getCoachTimezone()); }
function _endOfWeek() {
  const d = new Date();
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7)); // upcoming Sunday (0 if today is Sun)
  return localDateStr(d, getCoachTimezone());
}
function _dueFromChip(chip) { return chip === 'today' ? _today() : chip === 'week' ? _endOfWeek() : null; }
// The label rule lives in tasks/task-rules.js, shared in spirit with the
// coach app's Tasks screen (a parity test keeps the two in step).
function _dueTag(due) { return dueTag(due, _today()); }

// ── the My Tasks drawer ────────────────────────────────────────────
function _drawerOpen() { const d = document.getElementById('tasks-drawer'); return d && !d.classList.contains('hidden'); }

export function toggleTasksDrawer() {
  const d = document.getElementById('tasks-drawer'), s = document.getElementById('tasks-scrim');
  if (!d) return;
  const opening = d.classList.contains('hidden');
  // Closing via the BELL must tear down the floating due-popover too — only
  // the scrim path did, so it could outlive the drawer and sit over whatever
  // view came next (Clayton's screenshot, 2026-08-05).
  if (!opening) { _closeDuePop(); }
  d.classList.toggle('hidden', !opening);
  s?.classList.toggle('hidden', !opening);
  if (opening) {
    // Fresh open = fresh defaults: no leftover composer text, filter, history
    // view, or half-open note/sub-task editor from last time.
    _qaText = ''; _qaCaret = 0; _qaOff = new Set(); _qaActive = 0;
    _qaType = null; _qaDue = null; _qaRecur = null;
    _qaMore = false; _qaSubs = [];
    _filterClient = 'all'; _showDone = false; _noteEditId = null; _subAddId = null;
    renderTasksDrawer(); loadCoachTasks();
  }
}
export function closeTasksDrawer() {
  _closeDuePop();
  document.getElementById('tasks-drawer')?.classList.add('hidden');
  document.getElementById('tasks-scrim')?.classList.add('hidden');
}

const _TICK_SVG = '<svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>';

function _subs(t) { return Array.isArray(t.subtasks) ? t.subtasks : []; }

// The title WITH its inline @-token rendered in place (Clayton, 2026-08-05:
// the tag lives wherever it appears in the sentence — "call @Ray Snesrud
// about renewal"). The token is contenteditable="false" inside the editable
// title, so the caret treats it as one atom: backspace removes the whole
// name, and titleBlur reads textContent — token text included — making the
// visible text the single truth. Click on the token = the client's profile.
function _titleHtml(t, name) {
  const title = t.title || '';
  const idx = name ? title.indexOf(`@${name}`) : -1;
  if (idx < 0) return escHtml(title);
  const before = title.slice(0, idx);
  const after = title.slice(idx + name.length + 1);
  return `${escHtml(before)}<button class="ctk-client-inline" contenteditable="false" onclick="window.coachTasks.openClient('${escHtml(String(t.client_id))}')" title="Open ${escHtml(name)}'s profile">@${escHtml(name)}</button>${escHtml(after)}`;
}

// Inline-editable text (Clayton, 2026-08-05): click any title/note and TYPE —
// no edit button, no mode. contenteditable="plaintext-only" so pasted markup
// flattens; focus stores the original in data-orig (Escape restores it, and
// blur then sees "no change" and saves nothing). Enter commits via blur.
const _EDITABLE = `contenteditable="plaintext-only" spellcheck="false" onfocus="this.dataset.orig=this.textContent"`;

// A client's name is always a way into their profile — in a row's chip, in a
// group heading, on a completed row. One helper so the affordance, the title
// and the keyboard behaviour are identical wherever a name appears, and a real
// <button> so it is reachable by Tab rather than a div with an onclick.
function _clientLink(clientId, label, cls) {
  const name = _clientName(clientId);
  if (!clientId || !name) return `<span class="${cls}">${escHtml(label)}</span>`;
  return `<button type="button" class="${cls} is-link"`
    + ` onclick="window.coachTasks.openClient('${escHtml(String(clientId))}')"`
    + ` title="Open ${escHtml(name)}'s profile">${escHtml(label)}</button>`;
}

function _taskRow(t, groupBy = 'due') {
  const name = _clientName(t.client_id);
  // The heading already carries the grouped-by value; show the OTHER one —
  // a row must not repeat what its own heading already says.
  const lead = groupBy === 'client' ? (TYPE_LABEL[t.category] || TYPE_LABEL.admin) : name;
  // Only linkable when the chip is showing a CLIENT. Grouped by client the
  // chip shows the task's type, which is not a person and must not pretend to be.
  const leadChip = !lead ? ''
    : groupBy === 'client' ? `<span class="ctk-client">${escHtml(lead)}</span>`
    : _clientLink(t.client_id, lead, 'ctk-client');
  const tag = _dueTag(t.due_date);
  const recur = t.recur_rule && RECUR_LABEL[t.recur_rule]
    ? `<span class="ctk-recur" title="Repeats ${RECUR_LABEL[t.recur_rule]} — checking it off queues the next one">&#8635; ${RECUR_LABEL[t.recur_rule]}</span>` : '';
  // Notes render whenever text exists OR the pencil just asked for an empty
  // one to type into (_noteEditId) — the div itself is the editor now.
  const notes = (t.notes || _noteEditId === t.id)
    ? `<div class="ctk-notes ctk-editable" data-ph="Add a note…" ${_EDITABLE} onkeydown="window.coachTasks.editKey(event, this, true)" onblur="window.coachTasks.notesBlur(this)">${escHtml(t.notes || '')}</div>`
    : '';
  const due = tag
    ? `<button class="ctk-due ${tag.cls}" onclick="window.coachTasks.editDue('${escHtml(t.id)}', event)" title="Change due date">${escHtml(tag.text)}</button>`
    : `<button class="ctk-due ctk-due-add" onclick="window.coachTasks.editDue('${escHtml(t.id)}', event)">+ date</button>`;
  // Sub to-dos: always visible when present (the drawer is the whole surface —
  // no disclosure to hunt for). Checking the last one auto-completes the task.
  const subs = _subs(t);
  const subRows = subs.map(s => `<div class="ctk-sub" data-sid="${escHtml(s.id)}">
      <button class="ctk-subcheck${s.done ? ' done' : ''}" onclick="window.coachTasks.toggleSub('${escHtml(t.id)}','${escHtml(s.id)}')" aria-label="${s.done ? 'Undo' : 'Mark done'}">${s.done ? _TICK_SVG : ''}</button>
      <span class="ctk-sub-title ctk-editable${s.done ? ' is-done' : ''}" ${_EDITABLE} onkeydown="window.coachTasks.editKey(event, this)" onblur="window.coachTasks.subTitleBlur(this)">${escHtml(s.title)}</span>
      <button class="ctk-sub-del" onclick="window.coachTasks.removeSub('${escHtml(t.id)}','${escHtml(s.id)}')" title="Delete sub-task" aria-label="Delete sub-task">&times;</button>
    </div>`).join('');
  const subAdd = _subAddId === t.id
    ? `<input type="text" class="ctk-sub-add" placeholder="+ Add a sub-task…" onkeydown="window.coachTasks.subKey(event,'${escHtml(t.id)}',this)" onblur="window.coachTasks.subBlur('${escHtml(t.id)}',this)">`
    : '';
  const subsBlock = (subRows || subAdd) ? `<div class="ctk-subs">${subRows}${subAdd}</div>` : '';
  const doneCount = subs.filter(s => s.done).length;
  const subCount = subs.length
    ? `<span class="ctk-subcount" title="Sub-tasks done">${doneCount}/${subs.length}</span>` : '';
  return `<div class="ctk-item" data-id="${escHtml(t.id)}">
    <button class="ctk-check" onclick="window.coachTasks.toggleDone('${escHtml(t.id)}')" aria-label="Mark done"></button>
    <div class="ctk-main">
      <div class="ctk-title ctk-editable" ${_EDITABLE} onkeydown="window.coachTasks.editKey(event, this)" onblur="window.coachTasks.titleBlur(this)">${_titleHtml(t, name)}</div>
      ${notes}
      ${subsBlock}
      <div class="ctk-meta">${leadChip}${subCount}${recur}${due}</div>
    </div>
    <button class="ctk-edit" onclick="window.coachTasks.addSubStart('${escHtml(t.id)}')" title="Add sub-task" aria-label="Add sub-task"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="9" y1="6" x2="21" y2="6"/><line x1="9" y1="12" x2="21" y2="12"/><line x1="9" y1="18" x2="21" y2="18"/><polyline points="3 5 4 6 6 4"/><polyline points="3 11 4 12 6 10"/><polyline points="3 17 4 18 6 16"/></svg></button>
    <button class="ctk-edit" onclick="window.coachTasks.editNotes('${escHtml(t.id)}')" title="${t.notes ? 'Edit note' : 'Add note'}" aria-label="${t.notes ? 'Edit note' : 'Add note'}"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg></button>
    <button class="ctk-del" onclick="window.coachTasks.removeTask('${escHtml(t.id)}')" title="Delete" aria-label="Delete">&times;</button>
  </div>`;
}

// A done row: same tick/undo + delete as before; the completed-history view
// (withMeta) also shows the client chip and the completed date.
function _doneRow(t, withMeta) {
  const name = withMeta ? _clientName(t.client_id) : '';
  const when = withMeta && t.completed_at
    ? new Date(t.completed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  return `<div class="ctk-item ctk-item--done" data-id="${escHtml(t.id)}">
    <button class="ctk-check done" onclick="window.coachTasks.toggleDone('${escHtml(t.id)}')" aria-label="Undo"><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg></button>
    <div class="ctk-main">
      <div class="ctk-title">${escHtml(t.title || '')}</div>
      ${name ? `<div class="ctk-meta">${_clientLink(t.client_id, name, 'ctk-client')}</div>` : ''}
    </div>
    ${when ? `<span class="ctk-done-date">${escHtml(when)}</span>` : ''}
    <button class="ctk-del" onclick="window.coachTasks.removeTask('${escHtml(t.id)}')" title="Delete">&times;</button>
  </div>`;
}

// The field's own content: the coach's sentence with each parsed claim lit
// up IN PLACE (Clayton, 2026-08-05 — one sentence, not a plain input plus a
// read-only echo below it). Highlight-spans.js guarantees stripping every
// tag back out returns _qaText unchanged, character for character, which is
// what makes it safe to drop straight into a contenteditable's innerHTML —
// the coach can never end up seeing words they didn't type. parseTask's
// spans are SORTED, not disjoint (a type keyword can sit inside a client
// name, e.g. "@Amanda Block"), so the walk that decides what to strike
// lives in highlight-spans.js, unit-tested directly against that overlap.
function _quickFieldHtml(p) {
  return highlightSpans(_qaText, p.spans, _qaOff, escHtml);
}

// Everything in the composer BESIDES the field: the in-progress @-mention
// suggestion list, the restatement box, and the chip rows. All derived from
// the same _qaText/_qaCaret/_qaOff/_qaType/_qaDue as the field, but none of
// it holds the caret — unlike the field, it is safe to blow away and
// rebuild on every render (see renderQuickAdd).
function _quickRestHtml(p) {
  const sugg = mentionAt(_qaText, _qaCaret, _parseClients());
  const opts = sugg && !_qaOff.has('__sugg') ? _mentionMatches(sugg.query) : [];
  // An empty composer parses to category:'admin' by default — that is not a
  // claim the coach made, so no type chip should light for it.
  const hasText = !!_qaText.trim();
  // The restatement uses the same due-date wording as the rest of the
  // drawer (Today / Thu / 3d overdue) instead of a raw ISO date.
  const dueRestate = p.dueDate ? _dueTag(p.dueDate) : null;

  return `
      ${opts.length ? `<div class="ctk-sugg">${opts.map((c, i) =>
        `<button type="button" class="${i === _qaActive ? 'is-active' : ''}" onmousedown="event.preventDefault();window.coachTasks.qaPickMention('${escHtml(c.id)}')">${escHtml(_clientName(c.id))}</button>`).join('')}</div>` : ''}
      ${hasText ? `<div class="ctk-read">
        <span class="ctk-k">Task</span> <b>${escHtml(p.title || '—')}</b><br>
        <span class="ctk-k">Client</span> <b>${escHtml(_clientName(p.clientId) || '—')}</b><br>
        <span class="ctk-k">Due</span> <b>${escHtml(dueRestate ? dueRestate.text : '—')}</b><br>
        <span class="ctk-k">Type</span> <b>${escHtml(p.category)}</b>
        ${p.notes ? `<br><span class="ctk-k">Notes</span> <b>${escHtml(p.notes)}</b>` : ''}
      </div>` : ''}
      <div class="ctk-qa-row">
        <button class="ctk-chip${p.dueDate === _today() ? ' is-on' : ''}" onclick="window.coachTasks.qaSetDue('today')">Today</button>
        <button class="ctk-chip${p.dueDate === _endOfWeek() ? ' is-on' : ''}" onclick="window.coachTasks.qaSetDue('week')">This week</button>
        <input type="date" class="ctk-qa-date" id="ctk-qa-date" value="${escHtml(_qaDue || '')}" onchange="window.coachTasks.qaSetDue(this.value)">
        <button class="ctk-chip${_qaRecur ? ' is-on' : ''}" onclick="window.coachTasks.qaRecur()" title="Repeat — checking a repeating task off queues the next one">&#8635;${_qaRecur ? ' ' + RECUR_LABEL[_qaRecur] : ''}</button>
      </div>
      <div class="ctk-qa-row">
        ${TASK_TYPES.map(t => `<button class="ctk-chip${hasText && p.category === t ? ' is-on' : ''}" onclick="window.coachTasks.qaSetType('${t}')">${t}</button>`).join('')}
        <button class="ctk-chip${_qaMore ? ' is-on' : ''}" onclick="window.coachTasks.qaMore()" title="Sub-tasks at creation">+ sub-tasks</button>
      </div>
      ${_qaMore ? `
      <div class="ctk-qa-more">
        ${_qaSubs.map((s, i) => `<div class="ctk-sub"><span class="ctk-subcheck" aria-hidden="true"></span><span class="ctk-sub-title">${escHtml(s.title)}</span><button class="ctk-sub-del" onclick="window.coachTasks.qaSubRemove(${i})" aria-label="Remove sub-task">&times;</button></div>`).join('')}
        <input type="text" class="ctk-sub-add" id="ctk-qa-subadd" placeholder="+ Add a sub-task…" onkeydown="window.coachTasks.qaSubKey(event, this)">
      </div>` : ''}
  `;
}

// The composer's own markup — shared by the full-drawer render and the
// keystroke-only render (renderQuickAdd) below, so the two can never
// diverge. p is what pressing Add would create right now.
function _quickAddHtml() {
  const p = _qaParse();
  return `
    <div class="ctk-quick">
      <div id="ctk-quick-input" class="ctk-quick-field" contenteditable="plaintext-only" spellcheck="false"
           data-ph="+ Add a task… (@ tags a client)"
           onkeydown="window.coachTasks.qaKey(event, this)"
           oninput="window.coachTasks.qaInput(this)">${_quickFieldHtml(p)}</div>
      <div class="ctk-quick-rest">${_quickRestHtml(p)}</div>
    </div>`;
}

// Keystroke-only re-render: patches just the .ctk-quick subtree instead of
// the whole drawer. upgradeSelects (utils.js) attaches a plain `document`
// click listener, a capture-phase `window` scroll listener, and a `window`
// resize listener to EVERY upgraded <select>, and none of the three is ever
// removed (confirmed by reading utils.js directly — see the fix report).
// renderTasksDrawer rebuilds #ctk-filter-client on every call and then calls
// upgradeSelects(d) again, so routing every keystroke through the full
// drawer render leaked three global listeners per character typed, each
// retaining its own detached DOM subtree. The composer has no <select> of
// its own, so this path never needs upgradeSelects. It also leaves
// .ctk-list (the drawer's own overflow-y:auto scroll container) untouched,
// so the task list no longer jumps to the top on every keystroke.
//
// The field itself (#ctk-quick-input, a contenteditable) gets an EXTRA
// guard on top of that: its innerHTML is only ever overwritten when the
// highlighted HTML actually differs from what's already there. Most
// keystrokes don't change the parsed span set, so most keystrokes never
// touch the field's DOM at all — the caret the browser just placed is left
// completely alone, rather than destroyed and walked back to where it was.
// Everything else in .ctk-quick (suggestion list / restatement / chips)
// holds no caret, so it's cheap to just rebuild on every call.
function renderQuickAdd() {
  const d = document.getElementById('tasks-drawer');
  const wrap = d?.querySelector('.ctk-quick');
  const field = wrap?.querySelector('#ctk-quick-input');
  const rest = wrap?.querySelector('.ctk-quick-rest');
  if (!wrap || !field || !rest) { renderTasksDrawer(); return; } // composer not mounted yet — fall back to a full render
  const hadFocus = document.activeElement === field;
  const qs = wrap.querySelector('#ctk-qa-subadd');
  const qaSubDraft = qs ? qs.value : null;
  const qaSubHadFocus = !!qs && document.activeElement === qs;

  const p = _qaParse();
  const nextFieldHtml = _quickFieldHtml(p);
  if (field.innerHTML !== nextFieldHtml) {
    field.innerHTML = nextFieldHtml;
    if (hadFocus) setCaretOffset(field, _qaCaret);
  }
  rest.innerHTML = _quickRestHtml(p);

  if (_qaMore) {
    const nqs = wrap.querySelector('#ctk-qa-subadd');
    if (nqs && qaSubDraft != null) nqs.value = qaSubDraft;
    if (nqs && qaSubHadFocus) { nqs.focus(); nqs.setSelectionRange(nqs.value.length, nqs.value.length); }
  }
}

export function renderTasksDrawer() {
  const d = document.getElementById('tasks-drawer');
  if (!d) return;
  // Re-render must never eat in-flight typing (loads and chip/client picks
  // re-render the drawer). _qaText is the source of truth for the composer's
  // VALUE — the template below sets it straight from state — so only FOCUS
  // needs restoring here. The row-level and composer sub-task ADD inputs are
  // separate uncommitted-text fields (not part of _qaText) and still need
  // their drafts carried across the rebuild.
  const prevInput = d.querySelector('#ctk-quick-input');
  const hadFocus = !!prevInput && document.activeElement === prevInput;
  // Scoped to .ctk-list: the composer's own sub-task-add input (below) reuses
  // the same .ctk-sub-add class, and an unscoped query would grab whichever
  // one happens to render first when both are open at once.
  const prevSub = d.querySelector('.ctk-list .ctk-sub-add');
  const subDraft = prevSub ? prevSub.value : null;
  const subHadFocus = !!prevSub && document.activeElement === prevSub;
  const prevQaSub = d.querySelector('#ctk-qa-subadd');
  const qaSubDraft = prevQaSub ? prevQaSub.value : null;
  const qaSubHadFocus = !!prevQaSub && document.activeElement === prevQaSub;

  const open = _tasks.filter(t => t.status !== 'done');

  // Client filter options: clients that currently have open tasks (keep the
  // active pick listed even if its last open task was just checked off).
  const filterIds = [...new Set(open.map(t => t.client_id).filter(id => _clientName(id)))];
  if (_filterClient !== 'all' && !filterIds.includes(_filterClient)) {
    if (_clientName(_filterClient)) filterIds.push(_filterClient);
    else _filterClient = 'all';
  }
  filterIds.sort((a, b) => _clientName(a).localeCompare(_clientName(b)));

  const match = t => _filterClient === 'all' || t.client_id === _filterClient;
  const shown = open.filter(match);
  const today = _today();
  const doneAll = _tasks.filter(t => t.status === 'done' && match(t))
    .sort((a, b) => (b.completed_at || '').localeCompare(a.completed_at || ''));
  const doneToday = doneAll.filter(t => (t.completed_at || '').slice(0, 10) === today);

  // Grouping is a VIEW: every task in `shown` appears exactly once, in every
  // mode — never re-parented, never merged, never dropped (group-tasks.js).
  const groups = groupTasks(shown, _groupBy, { clientName: _clientName, today });
  let body = '';
  // ⚠️ The wrapper is load-bearing, not markup tidiness: a sticky heading is
  // bounded by its containing block, so flat headings in one scroll container
  // pile up at the top instead of displacing each other. The wrapper scopes
  // each heading to its own group. See .ctk-group-wrap in styles.css.
  groups.forEach(g => {
    // Grouped by client the heading IS a person, so it opens their profile.
    // Every task in the group shares one client_id — that is what the group key
    // is built from (group-tasks.js) — so the first row carries it exactly.
    // The "No client" group has none and stays plain text.
    const headId = _groupBy === 'client' ? (g.tasks[0] && g.tasks[0].client_id) : null;
    const heading = headId
      ? _clientLink(headId, g.label, 'ctk-group-label')
      : `<span class="ctk-group-label">${escHtml(g.label)}</span>`;
    body += `<section class="ctk-group-wrap">`
      + `<div class="ctk-group${g.label === 'Overdue' ? ' over' : ''}">${heading}</div>`
      + g.tasks.map(t => _taskRow(t, _groupBy)).join('')
      + `</section>`;
  });
  if (!shown.length) {
    body = _filterClient === 'all'
      ? `<div class="ctk-empty">No open tasks. Add one from a check-in or here 👆</div>`
      : `<div class="ctk-empty">No open tasks for ${escHtml(_clientName(_filterClient))}.</div>`;
  }
  if (_showDone) {
    body += doneAll.length
      ? `<section class="ctk-group-wrap"><div class="ctk-group">Completed (${doneAll.length})</div>${doneAll.map(t => _doneRow(t, true)).join('')}</section>`
      : `<section class="ctk-group-wrap"><div class="ctk-group">Completed</div><div class="ctk-empty">Nothing completed yet.</div></section>`;
  } else if (doneToday.length) {
    body += `<section class="ctk-group-wrap"><div class="ctk-group">Done today (${doneToday.length})</div>${doneToday.map(t => _doneRow(t, false)).join('')}</section>`;
  }

  const filter = filterIds.length ? `
    <div class="ctk-filter"><select class="crm-select" id="ctk-filter-client" onchange="window.coachTasks.setFilter(this.value)">
      <option value="all"${_filterClient === 'all' ? ' selected' : ''}>All clients</option>
      ${filterIds.map(id => `<option value="${escHtml(id)}"${_filterClient === id ? ' selected' : ''}>${escHtml(_clientName(id))}</option>`).join('')}
    </select></div>` : '';

  const quick = _quickAddHtml();

  // The pivot: same tasks, reassembled under a different heading. Belongs to
  // the full-drawer render (not the composer) — it never needs to rebuild on
  // a keystroke.
  const pivot = `<div class="ctk-pivot"><span>Group by</span>
    ${[['client', 'Client'], ['type', 'Type'], ['due', 'Due']].map(([m, label]) =>
      `<button class="ctk-chip${_groupBy === m ? ' is-on' : ''}" onclick="window.coachTasks.setGroupBy('${m}')">${escHtml(label)}</button>`).join('')}
  </div>`;

  d.innerHTML = `
    <div class="ctk-head"><span class="ctk-h-title">My Tasks</span><span class="ctk-h-count${_openCount() ? '' : ' hidden'}">${_openCount()} open</span><button class="ctk-h-x" onclick="window.coachTasks.closeDrawer()" aria-label="Close">&times;</button></div>
    ${quick}
    ${pivot}
    ${filter}
    <div class="ctk-list">${body}</div>
    <div class="ctk-foot"><button class="ctk-showdone" onclick="window.coachTasks.toggleShowDone()">${_showDone ? 'Hide completed' : 'Show completed'}</button></div>`;
  upgradeSelects(d);

  const input = d.querySelector('#ctk-quick-input');
  if (input && hadFocus) { input.focus(); setCaretOffset(input, _qaCaret); }
  if (_subAddId) {
    const si = d.querySelector('.ctk-list .ctk-sub-add');
    if (si) {
      if (subDraft != null) si.value = subDraft;
      if (subHadFocus || subDraft == null) { si.focus(); si.setSelectionRange(si.value.length, si.value.length); }
    }
  }
  if (_qaMore) {
    const qs = d.querySelector('#ctk-qa-subadd');
    if (qs && qaSubDraft != null) qs.value = qaSubDraft;
    if (qs && qaSubHadFocus) { qs.focus(); qs.setSelectionRange(qs.value.length, qs.value.length); }
  }
}

export async function quickAdd(fieldEl) {
  const raw = _qaText.trim();
  if (!raw) return; // nothing typed — bail with the (empty) text untouched
  // p IS what pressing Add creates — the restatement box in the render is
  // this exact same call, so there is nothing left to derive here. But the
  // parser LIFTS the client and date out of the title, so a tag/date-only
  // sentence ("@Mickey Mouse", "@Mickey Mouse tomorrow") parses to
  // title:"" — the old composer explicitly supported a tag-only task (a
  // bare "@Ray Snesrud" with sub-tasks hanging off it), so fall back to the
  // coach's raw sentence rather than send an empty title and 400.
  const p = _qaParse();
  const title = p.title.trim() || raw;
  // Composer extras ride the SAME create — no hunting the task down after.
  // A sub-task still sitting in the add box counts too (typed, not entered).
  const subDraft = (document.getElementById('ctk-qa-subadd')?.value || '').trim();
  if (subDraft) _qaSubs.push({ id: crypto.randomUUID(), title: subDraft, done: false });
  const subtasks = _qaSubs;
  // Issue the create call BEFORE clearing the composer. If we cleared first
  // and only then discovered something needed re-deriving from _qaText, the
  // sentence would already be gone — the exact bug this fix removes.
  const pending = createTask({ title, due: p.dueDate, category: p.category, clientId: p.clientId, source: 'manual', sourceRef: p.clientId, recurRule: _qaRecur, notes: p.notes, subtasks });
  _qaText = ''; _qaCaret = 0; _qaOff = new Set(); _qaActive = 0; _qaType = null; _qaDue = null;
  _qaMore = false; _qaSubs = [];
  renderTasksDrawer(); // clear the composer immediately; createTask's own
                        // re-render (below) then lands the task in the list.
  await pending;
  document.getElementById('ctk-quick-input')?.focus();
}

// ── Creation composer (+ sub-tasks): sub-items at add time ──
export function qaMore() {
  _qaMore = !_qaMore;
  renderTasksDrawer();
  (document.getElementById(_qaMore ? 'ctk-qa-subadd' : 'ctk-quick-input'))?.focus();
}
export function qaSubKey(ev, el) {
  if (ev.key === 'Enter') {
    ev.preventDefault();
    const title = (el.value || '').trim();
    if (!title) { document.getElementById('ctk-quick-input')?.focus(); return; }
    el.value = '';
    _qaSubs.push({ id: crypto.randomUUID(), title, done: false });
    renderTasksDrawer();
    document.getElementById('ctk-qa-subadd')?.focus();
  } else if (ev.key === 'Escape') { ev.stopPropagation(); el.blur(); }
}
export function qaSubRemove(i) {
  _qaSubs.splice(i, 1);
  renderTasksDrawer();
  document.getElementById('ctk-qa-subadd')?.focus();
}

// Cycle the quick-add repeat chip: off → daily → weekly → monthly → off.
export function qaRecur() {
  _qaRecur = RECUR_CYCLE[(RECUR_CYCLE.indexOf(_qaRecur) + 1) % RECUR_CYCLE.length];
  renderTasksDrawer();
  document.getElementById('ctk-quick-input')?.focus();
}

// Client chip → the client's profile (the drawer would sit over it).
export function openClient(id) {
  if (!id) return;
  closeTasksDrawer();
  window.showClientProfile?.(id);
}

// ── the composer's client suggestion list, as DERIVED state ────────
// Typing "@" starts an in-progress mention; the list is computed fresh on
// every render from _qaText/_qaCaret via mentionAt (Task 1) — there is no
// popup to open, track, or forget to close. A token that already names a
// real client is RESOLVED (mentionAt returns null), so the list is simply
// absent from the DOM the moment a pick lands.

// Same eligibility rules as the modal picker: no self-training account, no
// cancelled clients. (_clients is already demo-free.)
function _mentionMatches(query) {
  const q = (query || '').toLowerCase();
  return _clients
    .filter(c => c && c.id && !c.isSelfClient
      && c.status !== 'cancelled' && c.status !== 'pending_cancellation')
    .filter(c => !q || _clientName(c.id).toLowerCase().includes(q))
    .sort((a, b) => _clientName(a.id).localeCompare(_clientName(b.id)))
    .slice(0, 6);
}

export function qaInput(fieldEl) {
  // A contenteditable has no .value — the sentence lives in .textContent,
  // token spans and all (the highlighted markup is presentation only).
  _qaText = fieldEl.textContent || '';
  _qaCaret = caretOffset(fieldEl);
  _qaActive = 0;
  // A claim the coach switched off is re-armed once its text changes, so an
  // edit is never silently ignored.
  _qaOff = new Set([..._qaOff].filter(k => _qaParse().spans.some(s => s.kind === k)));
  renderQuickAdd();
}

export function qaKey(ev, fieldEl) {
  // The render reads _qaCaret, but only qaInput (a text EDIT) used to write
  // it. A caret move that isn't a text edit — click, ArrowLeft/Right,
  // Home/End — never fires 'input', so _qaCaret went stale while the live
  // caret kept moving. Refresh it on every keystroke so the two can never
  // diverge (this is also what the suggestion lookup below now reads,
  // instead of the live DOM value directly).
  _qaCaret = caretOffset(fieldEl);
  const sugg = mentionAt(_qaText, _qaCaret, _parseClients());
  // Suggestions respect the same '__sugg' dismissal the render uses — without
  // this, Escape-then-Enter would silently pick an invisible suggestion
  // instead of submitting the task.
  const opts = sugg && !_qaOff.has('__sugg') ? _mentionMatches(sugg.query) : [];
  if (opts.length && (ev.key === 'ArrowDown' || ev.key === 'ArrowUp')) {
    ev.preventDefault();
    _qaActive = (_qaActive + (ev.key === 'ArrowDown' ? 1 : -1) + opts.length) % opts.length;
    renderQuickAdd();
    return;
  }
  if (opts.length && (ev.key === 'Enter' || ev.key === 'Tab')) {
    ev.preventDefault();
    // _qaActive can still be stale from a larger, now-shrunk options list
    // (a caret move with no re-render in between) — clamp so opts[i] is
    // never undefined.
    const i = Math.min(_qaActive, opts.length - 1);
    qaPickMention(opts[i].id);
    return;
  }
  // Only intercept Escape while a suggestion is actually showing — otherwise
  // it falls through to the drawer's own Escape-to-close (initCoachTasks).
  if (opts.length && ev.key === 'Escape') { ev.stopPropagation(); _qaOff.add('__sugg'); renderQuickAdd(); return; }
  if (ev.key === 'Enter') { ev.preventDefault(); quickAdd(fieldEl); }
}

export function qaPickMention(id) {
  const el = document.getElementById('ctk-quick-input');
  const m = mentionAt(_qaText, el ? caretOffset(el) : _qaText.length, _parseClients());
  if (!m) return;
  const name = _clientName(id);
  _qaText = _qaText.slice(0, m.start) + `@${name} ` + _qaText.slice(m.end);
  _qaCaret = m.start + name.length + 2;
  _qaActive = 0;
  renderQuickAdd();
  const next = document.getElementById('ctk-quick-input');
  if (next) { next.focus(); setCaretOffset(next, _qaCaret); }
}

// A claim in the restatement box, un-set by clicking its token in the text.
// Turning a claim back ON also clears whatever explicit chip override was
// holding it off, so the token and the chips read the same value ONCE the
// coach interacts with either one again (click Today, then click the struck
// "by thursday" token — Due goes back to reading the typed date). That
// guarantee is about un-suppressing a claim, not about every keystroke: an
// explicit pick (_qaType/_qaDue) is applied LAST in _qaParse and beats
// whatever the parser reads from the live text, so typing a new date word
// after already picking one lights that word as a live span while the
// restatement keeps showing the earlier pick — until the coach clicks the
// chip or the token to reconcile them. Two different values CAN be on
// screen at once for exactly that window.
export function qaToggleSpan(kind) {
  if (_qaOff.has(kind)) {
    _qaOff.delete(kind);
    if (kind === 'due') _qaDue = null;
    if (kind === 'type') _qaType = null;
  } else {
    _qaOff.add(kind);
  }
  renderQuickAdd();
  document.getElementById('ctk-quick-input')?.focus();
}

// An explicit chip pick beats the parser. It is a SET, not an un-set, so —
// unlike the old code — it must never add to _qaOff (that struck through
// the coach's own typed words for a claim that was just being confirmed,
// not withdrawn). Clearing any stale override keeps the token and the chip
// reading the same value.
export function qaSetType(t) {
  _qaType = t;
  _qaOff.delete('type');
  renderQuickAdd();
  document.getElementById('ctk-quick-input')?.focus();
}
export function qaSetDue(v) {
  // The date input's onchange passes '' when the coach clears it — that must
  // mean "no date" outright, not "no override," or _qaParse falls through to
  // whatever date word is still sitting in the typed text and resurrects it.
  // So an empty value stays a SUPPRESSED claim ('due' kept in _qaOff), while
  // any real pick (today/week/an actual date) clears the suppression so the
  // override applies.
  const cleared = v === '';
  _qaDue = cleared ? null : v === 'today' ? _today() : v === 'week' ? _endOfWeek() : v;
  if (cleared) _qaOff.add('due'); else _qaOff.delete('due');
  renderQuickAdd();
  document.getElementById('ctk-quick-input')?.focus();
}

// ── drawer view controls ───────────────────────────────────────────
export function setFilter(v) { _filterClient = v || 'all'; renderTasksDrawer(); }
export function toggleShowDone() { _showDone = !_showDone; renderTasksDrawer(); }

// ── per-row due-date edit (small anchored popover, one click applies) ──
let _duePop = null;
function _ensureDuePop() {
  if (_duePop) return _duePop;
  const el = document.createElement('div');
  el.className = 'ctk-duepop'; el.id = 'ctk-duepop'; el.hidden = true;
  document.body.appendChild(el);
  _duePop = el;
  return el;
}
function _duePopOpen() { return _duePop && !_duePop.hidden; }

export function editDue(id, ev) {
  ev?.stopPropagation();
  const t = _tasks.find(x => x.id === id);
  if (!t) return;
  const pop = _ensureDuePop();
  const cur = t.due_date || '';
  const chip = !cur ? 'none' : cur === _today() ? 'today' : cur === _endOfWeek() ? 'week' : 'date';
  const on = c => chip === c ? ' is-on' : '';
  pop.innerHTML = `
    <button class="ctk-chip${on('today')}" data-due="today">Today</button>
    <button class="ctk-chip${on('week')}" data-due="week">This week</button>
    <button class="ctk-chip${on('date')}" data-due="date">Pick date</button>
    <button class="ctk-chip${on('none')}" data-due="none">No date</button>
    <input type="date" class="ctk-duepop-date" id="ctk-duepop-date" value="${escHtml(cur)}"${chip === 'date' ? '' : ' hidden'}>
    <span class="ctk-duepop-sep"></span>
    <span class="ctk-duepop-lbl">Repeats</span>
    ${[['', 'No'], ['daily', 'Daily'], ['weekly', 'Weekly'], ['monthly', 'Monthly']].map(([r, lbl]) =>
      `<button class="ctk-chip${(t.recur_rule || '') === r ? ' is-on' : ''}" data-recur="${r}">${lbl}</button>`).join('')}`;
  pop.querySelectorAll('[data-recur]').forEach(b => b.onclick = async () => {
    const rule = b.dataset.recur || null;
    _closeDuePop();
    if ((t.recur_rule || null) === rule) return;
    const prev = t.recur_rule;
    t.recur_rule = rule; // optimistic
    _afterChange();
    try { await _tapi('task-update', { body: { id, recur_rule: rule } }); }
    catch (e) { t.recur_rule = prev; _afterChange(); showToast(e.message, 'error'); }
  });
  // [data-due] only — the Repeats chips below share .ctk-chip and have their
  // own handlers; an unscoped grab here would overwrite them with the due
  // handler (whose undefined dataset.due would then CLEAR the date).
  pop.querySelectorAll('.ctk-chip[data-due]').forEach(b => b.onclick = () => {
    if (b.dataset.due === 'date') {
      pop.querySelectorAll('.ctk-chip[data-due]').forEach(x => x.classList.remove('is-on'));
      b.classList.add('is-on');
      const di = pop.querySelector('#ctk-duepop-date');
      di.hidden = false; di.focus();
      try { di.showPicker?.(); } catch { /* focus is enough */ }
      return;
    }
    _closeDuePop();
    _setDue(id, _dueFromChip(b.dataset.due));
  });
  pop.querySelector('#ctk-duepop-date').onchange = (e) => { const v = e.target.value || null; _closeDuePop(); _setDue(id, v); };
  pop.hidden = false;
  const r = ev?.currentTarget?.getBoundingClientRect?.() || { top: 100, bottom: 100, left: window.innerWidth - 320 };
  const pw = pop.offsetWidth, ph = pop.offsetHeight;
  let top = r.bottom + 6;
  if (top + ph > window.innerHeight - 8) top = Math.max(8, r.top - ph - 6);
  pop.style.top = top + 'px';
  pop.style.left = Math.max(12, Math.min(r.left, window.innerWidth - pw - 12)) + 'px';
  setTimeout(() => document.addEventListener('mousedown', _dueOutside, true), 0);
}
function _dueOutside(e) { if (_duePop && !_duePop.contains(e.target)) _closeDuePop(); }
function _closeDuePop() {
  if (_duePop) _duePop.hidden = true;
  document.removeEventListener('mousedown', _dueOutside, true);
}
async function _setDue(id, due) {
  const t = _tasks.find(x => x.id === id);
  if (!t || (t.due_date || null) === (due || null)) return;
  const prev = t.due_date;
  t.due_date = due; // optimistic
  _afterChange();
  try { await _tapi('task-update', { body: { id, due_date: due } }); }
  catch (e) { t.due_date = prev; _afterChange(); showToast(e.message, 'error'); }
}

// ── inline editing (click the text and type; no edit mode) ─────────
// Shared keys: Enter commits via blur (Shift+Enter = newline in notes only);
// Escape restores the focus-time original, so the blur save sees no change.
export function editKey(ev, el, multiline) {
  if (ev.key === 'Enter' && !(multiline && ev.shiftKey)) { ev.preventDefault(); el.blur(); }
  else if (ev.key === 'Escape') {
    ev.stopPropagation();
    el.textContent = el.dataset.orig ?? el.textContent;
    el.blur();
  }
}

export async function titleBlur(el) {
  const id = el.closest('.ctk-item')?.dataset.id;
  const t = _tasks.find(x => x.id === id);
  if (!t) return;
  const name = _clientName(t.client_id);
  // textContent flattens the inline token back to its "@Full Name" text ONLY
  // on the path where the token is actually rendered (t.title contains
  // "@Name") — parseTask LIFTS the client span out of the title on create,
  // so a plain-titled tagged task ("update nutrition", client_id set) has NO
  // token in the text at all. So the visible string is the saved string only
  // on that tag-in-title fallback path, not in general.
  const val = (el.textContent || '').replace(/\s+/g, ' ').trim();
  if (!val) { el.innerHTML = _titleHtml(t, name); return; } // a task needs a title — empty reverts
  // Un-tagging is relative to what the title HAD, not to what it lacks — a
  // title that never carried "@Name" (the common case, since parseTask lifts
  // the tag out at create time) must never be read as "the token was
  // deleted." Only a title that DID have the token, and no longer does,
  // means the coach removed it.
  const had = !!(name && (t.title || '').includes(`@${name}`));
  const untag = had && !val.includes(`@${name}`);
  if (val === (t.title || '') && !untag) { el.innerHTML = _titleHtml(t, name); return; }
  const prev = { title: t.title, clientId: t.client_id };
  t.title = val; // optimistic
  if (untag) t.client_id = null;
  _afterChange();
  const body = { id, title: val };
  // source_ref keeps recording where the task came from — only the client
  // field (client_id) is cleared by untagging.
  if (untag) body.client_id = null;
  try { await _tapi('task-update', { body }); }
  catch (e) { t.title = prev.title; t.client_id = prev.clientId; _afterChange(); showToast(e.message, 'error'); }
}

export async function subTitleBlur(el) {
  const t = _tasks.find(x => x.id === el.closest('.ctk-item')?.dataset.id);
  const sid = el.closest('.ctk-sub')?.dataset.sid;
  const cur = t ? _subs(t).find(s => s.id === sid) : null;
  if (!cur) return;
  const val = (el.textContent || '').trim();
  if (!val) { el.textContent = cur.title; return; } // empty reverts — deleting stays on the ×
  if (val === cur.title) { el.textContent = val; return; }
  await _saveSubs(t, _subs(t).map(s => s.id === sid ? { ...s, title: val } : s));
}

export async function notesBlur(el) {
  const id = el.closest('.ctk-item')?.dataset.id;
  const t = _tasks.find(x => x.id === id);
  if (!t) return;
  if (_noteEditId === id) _noteEditId = null;
  const val = (el.textContent || '').trim() || null;
  if ((t.notes || null) === val) { renderTasksDrawer(); return; } // drops an abandoned empty div
  const prev = t.notes;
  t.notes = val; // optimistic
  _afterChange();
  try { await _tapi('task-update', { body: { id, notes: val } }); }
  catch (e) { t.notes = prev; _afterChange(); showToast(e.message, 'error'); }
}

// The pencil's only remaining job: summon an empty notes div to type into
// when the task has none yet (existing notes are click-to-edit directly).
export function editNotes(id) {
  _noteEditId = id;
  renderTasksDrawer();
  document.getElementById('tasks-drawer')
    ?.querySelector(`.ctk-item[data-id="${id}"] .ctk-notes`)?.focus();
}

// This client's OPEN tasks (for the in-check-in "Tasks for {client}" view).
function _clientOpenTasks(clientId) {
  return clientId ? _tasks.filter(t => t.client_id === clientId && t.status !== 'done') : [];
}
// Fill any header count chips (the "Tasks · N" badge in a check-in) by client.
export function refreshClientTaskCounts() {
  document.querySelectorAll('[data-ctk-count]').forEach(el => {
    const n = _clientOpenTasks(el.dataset.cid).length;
    el.textContent = n;
    el.classList.toggle('hidden', n === 0);
  });
}
// After any change: badge, header counts, and any open surface (drawer/popover).
function _afterChange() {
  _updateBadge();
  refreshClientTaskCounts();
  if (_drawerOpen()) renderTasksDrawer();
  if (_popOpen()) _renderPopover();
}

// Exported (2026-09-30) so the check-in review's glance page can add a task
// with its notes in one call; also on window.coachTasks below.
export async function createTask({ title, due = null, category = 'admin', clientId = null, source = 'manual', sourceRef = null, notes = null, recurRule = null, subtasks = [] }) {
  try {
    const { task } = await _tapi('task-create', { body: { title, category, client_id: clientId, source, source_ref: sourceRef, due_date: due, notes, recur_rule: recurRule, subtasks } });
    if (task) _tasks.unshift(task);
    _afterChange();
    return task;
  } catch (e) { showToast(e.message || 'Could not add task', 'error'); }
}

export async function toggleDone(id) {
  const t = _tasks.find(x => x.id === id);
  if (!t) return;
  const next = t.status === 'done' ? 'todo' : 'done';
  // Completing the parent completes its sub-list too — "the task is done"
  // means all of it. Reopening leaves the subs as they are: the coach
  // unchecks exactly what still needs doing.
  const prevSubs = _subs(t);
  const nextSubs = subsOnStatusChange(prevSubs, next);
  const prevStatus = t.status; const prevAt = t.completed_at;
  t.status = next; t.completed_at = next === 'done' ? new Date().toISOString() : null; // optimistic
  t.subtasks = nextSubs;
  _afterChange();
  const body = { id, status: next, tz: getCoachTimezone() };
  if (nextSubs !== prevSubs) body.subtasks = nextSubs;
  try {
    const res = await _tapi('task-update', { body });
    // Completing a repeating task spawns its next occurrence server-side —
    // surface it without waiting for a reload.
    if (res.spawned) { _tasks.unshift(res.spawned); _afterChange(); }
  }
  catch (e) { t.status = prevStatus; t.completed_at = prevAt; t.subtasks = prevSubs; _afterChange(); showToast(e.message, 'error'); }
}

// ── sub to-dos ─────────────────────────────────────────────────────
// One rule, both directions (tasks/task-rules.js subStatusPatch).
const _subStatusPatch = subStatusPatch;

async function _saveSubs(t, subs, patch = {}) {
  const prev = { subtasks: _subs(t), status: t.status, completed_at: t.completed_at };
  t.subtasks = subs;
  if (patch.status) {
    t.status = patch.status;
    t.completed_at = patch.status === 'done' ? new Date().toISOString() : null;
  }
  _afterChange();
  try {
    const res = await _tapi('task-update', { body: { id: t.id, subtasks: subs, tz: getCoachTimezone(), ...patch } });
    // Checking the last sub of a REPEATING task auto-completes it — which
    // spawns the next occurrence, same as the main disc.
    if (res.spawned) { _tasks.unshift(res.spawned); _afterChange(); }
  }
  catch (e) {
    t.subtasks = prev.subtasks; t.status = prev.status; t.completed_at = prev.completed_at;
    _afterChange(); showToast(e.message, 'error');
  }
}

export async function toggleSub(taskId, subId) {
  const t = _tasks.find(x => x.id === taskId);
  if (!t) return;
  const subs = _subs(t).map(s => s.id === subId ? { ...s, done: !s.done } : s);
  await _saveSubs(t, subs, _subStatusPatch(t, subs));
}

export async function removeSub(taskId, subId) {
  const t = _tasks.find(x => x.id === taskId);
  if (!t) return;
  const subs = _subs(t).filter(s => s.id !== subId);
  // Deleting the last open sub can complete the parent (same rule as checking
  // it) — but an emptied list must never auto-complete anything.
  await _saveSubs(t, subs, subs.length ? _subStatusPatch(t, subs) : {});
}

export function addSubStart(id) {
  _subAddId = id;
  renderTasksDrawer();
}
export function subKey(ev, id, el) {
  if (ev.key === 'Enter') { ev.preventDefault(); _commitSub(id, el); }
  else if (ev.key === 'Escape') { ev.stopPropagation(); _subAddId = null; renderTasksDrawer(); }
}
export function subBlur(id, el) {
  if (_subAddId !== id) return; // Escape already closed it
  const val = (el.value || '').trim();
  if (val) { _commitSub(id, el, { close: true }); }
  else { _subAddId = null; renderTasksDrawer(); }
}
async function _commitSub(id, el, { close } = {}) {
  const t = _tasks.find(x => x.id === id);
  const title = (el.value || '').trim();
  if (!t || !title) return;
  el.value = '';
  if (close) _subAddId = null;
  // Enter keeps the input open for rapid entry; blur closes it.
  await _saveSubs(t, [..._subs(t), { id: crypto.randomUUID(), title, done: false }]);
}

export async function removeTask(id) {
  const idx = _tasks.findIndex(x => x.id === id);
  if (idx < 0) return;
  const [removed] = _tasks.splice(idx, 1); // optimistic
  _afterChange();
  try { await _tapi('task-delete', { body: { id } }); }
  catch (e) { _tasks.splice(idx, 0, removed); _afterChange(); showToast(e.message, 'error'); }
}

// ── the in-context "Tasks for {client}" popover (opened from a check-in) ──
// Shows THIS client's open tasks (check them off in context) + an add form, so
// while reviewing a client you see continuity and don't double-add.
let _pop = null;
let _popCtx = null;
let _popChip = 'week';
function _ensurePop() {
  if (_pop) return _pop;
  const el = document.createElement('div');
  el.className = 'ctk-pop'; el.id = 'ctk-pop'; el.hidden = true;
  document.body.appendChild(el);
  _pop = el;
  return el;
}
function _popOpen() { return _pop && !_pop.hidden; }

// ctx: { clientId, clientName, checkinId, anchorRect }
export function openClientTasks(ctx) {
  _popCtx = ctx || {};
  _popChip = 'week';
  const pop = _ensurePop();
  _renderPopover();
  pop.hidden = false;
  const r = _popCtx.anchorRect || { bottom: 80, right: window.innerWidth - 20 };
  const pw = pop.offsetWidth;
  pop.style.top = (r.bottom + 8) + 'px';
  pop.style.left = Math.max(12, Math.min(r.right - pw, window.innerWidth - pw - 12)) + 'px';
  pop.querySelector('#ctk-pop-input')?.focus();
  setTimeout(() => document.addEventListener('mousedown', _popOutside, true), 0);
}

function _renderPopover() {
  if (!_pop) return;
  const name = _popCtx.clientName || 'this client';
  const open = _clientOpenTasks(_popCtx.clientId);
  const list = open.length
    ? open.map(t => {
        const tag = _dueTag(t.due_date);
        return `<div class="ctk-pop-item"><button class="ctk-check" onclick="window.coachTasks.toggleDone('${escHtml(t.id)}')" aria-label="Mark done"></button><span class="ctk-pop-item-title">${escHtml(t.title || '')}</span>${tag ? `<span class="ctk-due ${tag.cls}">${escHtml(tag.text)}</span>` : ''}<button class="ctk-del" onclick="window.coachTasks.removeTask('${escHtml(t.id)}')" title="Delete task (created by accident?)" aria-label="Delete">&times;</button></div>`;
      }).join('')
    : `<div class="ctk-pop-empty">No open tasks for ${escHtml(name)} yet.</div>`;
  const on = c => _popChip === c ? ' is-on' : '';
  _pop.innerHTML = `
    <div class="ctk-pop-head"><span class="ctk-pop-title">Tasks for ${escHtml(name)} · just for you</span><button class="ctk-pop-x" id="ctk-pop-close" aria-label="Close">&times;</button></div>
    <div class="ctk-pop-list">${list}</div>
    <div class="ctk-pop-add">
      <input type="text" class="ctk-pop-input" id="ctk-pop-input" placeholder="＋ Add a task…">
      <div class="ctk-pop-due"><span class="ctk-pop-lbl">Due</span>
        <button class="ctk-chip${on('today')}" data-chip="today">Today</button>
        <button class="ctk-chip${on('week')}" data-chip="week">This week</button>
        <button class="ctk-chip${on('date')}" data-chip="date">Pick date</button>
        <button class="ctk-chip${on('none')}" data-chip="none">No date</button>
        <input type="date" class="ctk-pop-date" id="ctk-pop-date"${_popChip === 'date' ? '' : ' hidden'}>
      </div>
      <div class="ctk-pop-foot"><button class="ctk-pb ctk-pb--go" id="ctk-pop-add">Add task</button></div>
    </div>`;
  _pop.querySelector('#ctk-pop-close').onclick = _closePop;
  _pop.querySelectorAll('.ctk-chip').forEach(b => b.onclick = () => {
    _pop.querySelectorAll('.ctk-chip').forEach(x => x.classList.remove('is-on'));
    b.classList.add('is-on'); _popChip = b.dataset.chip;
    const d = _pop.querySelector('#ctk-pop-date'); d.hidden = _popChip !== 'date'; if (_popChip === 'date') d.focus();
  });
  _pop.querySelector('#ctk-pop-add').onclick = _submitPop;
  _pop.querySelector('#ctk-pop-input').onkeydown = (e) => { if (e.key === 'Enter') { e.preventDefault(); _submitPop(); } if (e.key === 'Escape') _closePop(); };
}

function _popOutside(e) { if (_pop && !_pop.contains(e.target)) _closePop(); }
function _closePop() { if (_pop) _pop.hidden = true; document.removeEventListener('mousedown', _popOutside, true); }

// Add from the popover, then KEEP it open (re-rendered) so the coach sees it
// land and can add another — check-off happens right here too.
async function _submitPop() {
  const input = _pop?.querySelector('#ctk-pop-input');
  const title = (input?.value || '').trim();
  if (!title) { input?.focus(); return; }
  const due = _popChip === 'date' ? (_pop.querySelector('#ctk-pop-date').value || null) : _dueFromChip(_popChip);
  await createTask({ title, due, category: 'admin', clientId: _popCtx.clientId || null, source: 'checkin', sourceRef: _popCtx.clientId || null });
  _pop?.querySelector('#ctk-pop-input')?.focus(); // _afterChange re-rendered; refocus the fresh input
}

// ── init: wire the scrim, expose the API, first load ───────────────
export function initCoachTasks() {
  if (!document.getElementById('tasks-drawer')) {
    const scrim = document.createElement('div'); scrim.id = 'tasks-scrim'; scrim.className = 'tasks-scrim hidden';
    const drawer = document.createElement('div'); drawer.id = 'tasks-drawer'; drawer.className = 'tasks-drawer hidden';
    document.body.append(scrim, drawer);
  }
  document.getElementById('tasks-scrim')?.addEventListener('click', closeTasksDrawer);
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape' || !_drawerOpen()) return;
    // The suggestion list has no popup lifecycle of its own to close here —
    // qaKey handles its own Escape (with stopPropagation) while a suggestion
    // is showing, so this only ever sees Escape with nothing left to catch.
    if (_duePopOpen()) _closeDuePop();
    else closeTasksDrawer();
  });
  window.coachTasks = {
    toggleDrawer: toggleTasksDrawer, closeDrawer: closeTasksDrawer,
    quickAdd, toggleDone, removeTask, openClientTasks, refreshClientTaskCounts, createTask,
    qaInput, qaKey, qaPickMention, qaToggleSpan, qaSetType, qaSetDue, qaRecur, qaMore, qaSubKey, qaSubRemove, openClient, setFilter, setGroupBy, toggleShowDone,
    editDue, editNotes, editKey, titleBlur, subTitleBlur, notesBlur,
    toggleSub, removeSub, addSubStart, subKey, subBlur,
  };
  loadCoachTasks();
  subscribeTasksLive();
}

// ── Live: a task changed somewhere else ─────────────────────────────
//
// ⚠️ ONE LIST, TWO SCREENS (2026-10-06). The coach app's Tasks screen works on
// these same rows, and Clayton asked for the two to stay in step live ("make
// sure the tasks screen syncs realtime/directly with the crm one so its
// unified"). Every write to coach_tasks broadcasts { table, operation, id } on
// the private topic tasks:<owner id> (migration
// 20261006130000_tasks_tell_their_owner_when_they_change.sql); only the owner
// may listen. The payload is ids only, so this reloads through task-list,
// the same read the drawer always uses.
//
// ⚠️ NEVER UNDER THE COACH'S FINGERS. renderTasksDrawer already carries the
// composer's text and focus across a rebuild, but a change from the phone
// lands whenever it lands: while a title, note or sub-task is being typed in
// the drawer, the reload waits until focus leaves that box.
let _liveChannel = null;
let _liveTimer = null;
let _liveWaiting = false;
const LIVE_DEBOUNCE_MS = 400;

function _typingInDrawer() {
  const d = document.getElementById('tasks-drawer');
  const a = document.activeElement;
  return !!(d && a && d.contains(a) && (a.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName)));
}

export function liveReloadSoon() {
  clearTimeout(_liveTimer);
  _liveTimer = setTimeout(() => {
    if (_typingInDrawer()) {
      if (!_liveWaiting) {
        _liveWaiting = true;
        document.getElementById('tasks-drawer')?.addEventListener('focusout', () => {
          _liveWaiting = false;
          liveReloadSoon();
        }, { once: true });
      }
      return;
    }
    loadCoachTasks();
  }, LIVE_DEBOUNCE_MS);
}

// ⚠️ OPENED ONCE PER PAGE, NEVER TORN DOWN (the meetings.js / sessions.js
// shape). The topic is fixed by the database trigger, and closing then asking
// for the same topic again can hand back the closing channel, whose subscribe
// never joins (a-realtime-channel-is-removed-not-just-unsubscribed.test.mjs).
// Concurrent first calls share one open (`_liveOpening`).
let _liveOpening = null;
export function subscribeTasksLive() {
  if (_liveChannel) return Promise.resolve();
  if (!_liveOpening) _liveOpening = _openTasksLive().finally(() => { _liveOpening = null; });
  return _liveOpening;
}
async function _openTasksLive() {
  const supabase = getSupabase();
  if (!supabase) return;
  let uid = null;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    uid = session?.user?.id || null;
  } catch { /* signed out: nothing to listen for */ }
  if (!uid) return;
  // Same join as the Lead Engine's private channel (lead-engine/inbox.js).
  try { await supabase.realtime.setAuth(); } catch { /* older client — ignore */ }
  _liveChannel = supabase
    .channel(`tasks:${uid}`, { config: { private: true } })
    .on('broadcast', { event: 'INSERT' }, liveReloadSoon)
    .on('broadcast', { event: 'UPDATE' }, liveReloadSoon)
    .on('broadcast', { event: 'DELETE' }, liveReloadSoon)
    .subscribe();
}
